import { defineStore } from 'pinia'
import { useSpace } from '@/store/modules/App/space'
import { client } from '@/services/client'
import createContentUrl from '@/utils/createContentUrl'
import {
  sanitizeStoriesToApps,
  sanitizeStoryToApp,
} from '@utils/sanitizeStoriesToApps'
import StoryblokClient from 'storyblok-js-client'
import type {
  App,
  AppsResponse,
  AppCategory,
  AppCategories,
  AppProvision,
} from '@/types'
import type { ISbResult } from 'storyblok-js-client'

import { DEFAULT_PAGINATION } from '@/constants/pagination'
import type { AppApiParams } from '@/components/ContentManager/ListApps/types'

export type AppProvisionPayload = {
  appId: string | number
  spaceId: string | number
  appData?: Record<string, string | number | boolean>
  appSettings?: Record<string, string>
  region?: string
}

export type AppRating = {
  id: number
  app_id: number
  app_slug: string
  rating: number
  notify: boolean
}

export type State = {
  app: App
  apps: App[]
  appCategories: AppCategory[]
  appProvision: App
  totalApps: number
}

const defaultState = (): State => ({
  app: {} as App,
  apps: [] as App[],
  appCategories: [],
  appProvision: {} as App,
  totalApps: 0,
})

export const useApps = defineStore('apps', {
  state: defaultState,
  getters: {
    getApp: (state: State): App => state.app,
    getApps: (state: State): App[] => state.apps,
    getAppCategories: (state: State): AppCategory[] => state.appCategories,
    getAppProvision: (state: State): App => state.appProvision,
    getTotalApps: (state: State): number => state.totalApps,
  },
  actions: {
    setApp(app: App): App {
      return (this.app = app)
    },

    setApps(response: {
      headers: { total: number }
      data: AppsResponse
    }): void {
      this.totalApps = response.headers.total
      this.apps = response.data.apps
    },

    setAppCategories(appCategories: AppCategory[]): AppCategory[] {
      return (this.appCategories = appCategories)
    },

    setAppProvision(appProvision: App): App {
      return (this.appProvision = appProvision)
    },

    async loadAppRating(): Promise<{
      app_ratings: AppRating[]
    }> {
      const { data } = await client.get('app_ratings')
      return data
    },

    async saveAppRating(payload: {
      rating?: number | undefined
      notify?: boolean | undefined
      app_slug: string
    }): Promise<AppRating> {
      const { data } = await client.post('app_ratings', payload)
      return data
    },

    async loadApp({
      appId,
      spaceId,
    }: Partial<AppProvisionPayload>): Promise<App> {
      const spaceIdParam = spaceId ? `?space_id=${spaceId}` : ''
      const { data } = await client.get(`/apps/${appId}${spaceIdParam}`, {
        region: useSpace().region,
      })
      this.setApp(data.app)
      return data
    },

    async loadApps(payload: {
      page?: number
      spaceId: string
      params: AppApiParams
    }): Promise<AppsResponse> {
      const { page = DEFAULT_PAGINATION.currentPage, spaceId } = payload
      const { category, type, external } = payload.params
      let categoryQuery = ''
      const contentParam = createContentUrl()

      if (external) {
        const sbClient = new StoryblokClient({
          accessToken: contentParam.token,
        })

        return await sbClient
          .get('cdn/stories/', {
            starts_with: 'external-apps/',
            page: page,
            version: contentParam.version,
            cv: contentParam.cv,
          })
          .then((res: ISbResult) => {
            this.setApps({
              headers: { total: res.total || 0 },
              data: {
                apps: sanitizeStoriesToApps(res.data.stories) as App[],
              },
            })
            return res.data.stories
          })
      }

      if (category && category !== 'all') {
        categoryQuery = `&category=${category}`
      }

      const typeQuery = type ? `&type=${type}` : ''

      const urlQuery = `${categoryQuery}${typeQuery}`

      const response = await client.get(
        `/apps/?space_id=${spaceId}${urlQuery}&page=${page}`,
        { region: useSpace().region },
      )

      this.setApps({
        headers: { total: response.headers.total },
        data: response.data,
      })

      return response.data
    },

    async loadAppCategories(): Promise<AppCategories> {
      const { data } = await client.get(`/app_categories/`, {
        region: useSpace().region,
      })
      this.setAppCategories(data.app_categories)

      return data
    },

    async loadAppProvision({
      appId,
      spaceId,
    }: Partial<AppProvisionPayload>): Promise<{
      app: App
      app_provision: AppProvision
      granted: boolean
    }> {
      const { data } = await client.get(
        `spaces/${spaceId}/app_provisions/${appId}`,
        {
          region: useSpace().region,
          withCredentials: true,
        },
      )
      this.setAppProvision(data)
      return data
    },

    async updateAppProvision({
      appId,
      appData = {},
      spaceId,
      appSettings = {},
    }: AppProvisionPayload): Promise<AppProvision> {
      return await client.put(
        `spaces/${spaceId}/app_provisions/${appId}`,
        {
          app_provision: {
            app_id: appId,
            public_config: appData,
            space_level_settings: appSettings,
          },
        },
        { region: useSpace().region },
      )
    },

    async installApp({
      appId,
      spaceId,
      region,
    }: Partial<AppProvisionPayload>): Promise<App> {
      const selectedRegion = region || useSpace().region
      return await client.post(
        `spaces/${spaceId}/app_provisions/`,
        {
          app_provision: {
            app_id: appId,
          },
        },
        { region: selectedRegion },
      )
    },

    async uninstallApp({
      appId,
      spaceId,
    }: Partial<AppProvisionPayload>): Promise<void> {
      return await client.delete(`spaces/${spaceId}/app_provisions/${appId}`, {
        region: useSpace().region,
      })
    },

    async loadExternalApp(appId: string): Promise<void> {
      const contentParam = createContentUrl()

      const sbClient = new StoryblokClient({
        accessToken: contentParam.token,
      })

      return await sbClient
        .get('cdn/stories/', { by_ids: appId, version: contentParam.version })
        .then((res: ISbResult) => {
          this.setApp(sanitizeStoryToApp(res.data?.stories[0]) as App)
        })
    },

    async loadAppBridgeToken({
      appId,
      spaceId,
    }: {
      appId: number
      spaceId: string
    }): Promise<string> {
      const res = await client.post(
        `spaces/${spaceId}/app_validations/`,
        {
          app_id: appId,
        },
        {
          region: useSpace().region,
        },
      )

      return res.data.token
    },
  },
})
